import { BiometryTypesCustom, User } from './../types/user'
import { defineStore } from 'pinia'
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js'
import { toRaw } from 'vue'
import { usePlaidItemsStore } from './plaidItem'
import { useMessagesStore } from './messages'
import { useCategoriesStore } from './category'
import { useTransactionsStore } from './transaction'
import axios from 'axios'
import {
  BiometricAuth,
  BiometryType,
} from '@aparajita/capacitor-biometric-auth'
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin'
import { useCustomAlert } from '@/composables/useCustomAlert'
import { usePostHog } from '@/composables/usePostHog'
import { getStoredUtmParams } from '@/composables/useCampaignTracking'

const userPool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_USER_POOL_ID,
  ClientId: import.meta.env.VITE_USER_POOL_APP_CLIENT_ID,
})

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: null as string | null,
    refreshToken: null as string | null,
    user: null as User | null,
    interceptor: null as number | null,
    onboardingComplete: true,
    refreshTokenBiometric: null as string | null,
    usernameBiometric: null as string | null,
    signupData: null as {
      firstName: string
      lastName: string
      email: string
      password?: string
      hearAboutBalance?: string
    } | null,
  }),
  getters: {
    isLoggedIn: (state) => state.token !== null,
    hasActiveSubscription: (state) => {
      return (
        state.user?.stripeSubscriptionStatus === 'active' ||
        state.user?.stripeSubscriptionStatus === 'trialing'
      )
    },
    hasExpiredSubscription: (state) => {
      return (
        state.user?.stripeSubscriptionStatus != undefined &&
        !(
          state.user?.stripeSubscriptionStatus === 'active' ||
          state.user?.stripeSubscriptionStatus === 'trialing'
        )
      )
    },
    canAddAccount: (state) => {
      const itemsStore = usePlaidItemsStore()

      const userStripePriceId = state.user?.stripePriceId

      // const accountLimits: Record<string, number> = {
      //   [import.meta.env.VITE_STRIPE_PRICE_STANDARD]: 3,
      //   [import.meta.env.VITE_STRIPE_PRICE_PRO]: 10,
      //   [import.meta.env.VITE_STRIPE_PRICE_SUPPORTER]: 20,
      // }

      let limit = 0

      if (
        userStripePriceId &&
        ['active', 'trialing'].includes(
          state.user?.stripeSubscriptionStatus ?? '',
        )
      ) {
        // limit = accountLimits[userStripePriceId]
        limit = 10
      }
      // console.log(limit, state.user?.stripeSubscriptionStatus)

      return itemsStore.items.length < limit
    },
  },
  actions: {
    async login(username: string, password: string) {
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      })

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      })

      const { showError } = useCustomAlert()

      return await new Promise((resolve, reject) => {
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: async (result: CognitoUserSession) => {
            this.token = result.getIdToken().getJwtToken()
            this.refreshToken = result.getRefreshToken().getToken()
            this.setAuthHeader()
            this.setInterceptor()
            await this.getUser()
            if (this.user?.cognitoId) {
              console.log('CognitoId: ', this.user.cognitoId)
              // Identify user in PostHog with email
              const { identify } = usePostHog()
              if (this.user.email) {
                identify(this.user.email, {
                  cognitoId: this.user.cognitoId,
                  email: this.user.email,
                  firstName: this.user.firstName,
                  lastName: this.user.lastName,
                })
              }
            }
            resolve(this.token)
          },

          onFailure: (err) => {
            if (err.code === 'UserNotConfirmedException') {
              this.signupData = {
                firstName: '',
                lastName: '',
                email: username,
                password: password,
              }
              this.user = new User(username, '', '', '', '', '', '', '')
              this.onboardingComplete = false
              reject({
                code: 'UserNotConfirmedException',
                redirect: '/auth/verify-email',
                autoSend: true,
              })
            } else {
              showError(err.message || JSON.stringify(err))
              reject()
            }
          },
        })
      })
    },
    async getUser() {
      try {
        let user = await this.axios.get(`/user`)
        this.user = new User(
          user.data.email,
          user.data.firstName,
          user.data.lastName,
          user.data.cognitoId,
          user.data.stripeCustomerId,
          user.data.stripeSubscriptionId,
          user.data.stripeSubscriptionStatus,
          user.data.stripePriceId,
        )
        await this.ionicStorage.set('user', toRaw(this.user))
        await this.ionicStorage.set('token', this.token)
        await this.ionicStorage.set('refreshToken', this.refreshToken)

        // Identify user in PostHog when getting user data (handles existing sessions)
        const { identifyExistingUser } = usePostHog()
        identifyExistingUser(this.user)

        return user.data
      } catch (error) {
        console.error(error)
      }
    },
    async createCheckoutSession(
      priceId: string,
      mobile: boolean,
      trial: boolean = false,
    ) {
      try {
        let session = await this.axios.post(`/users/createCheckoutSession`, {
          priceId,
          mobile,
          trial,
        })
        return session.data
      } catch (error) {
        console.error(error)
      }
    },
    async signup(
      firstName: string,
      lastName: string,
      email: string,
      password: string,
      hearAboutBalance: string,
      agreeToTerms: boolean,
    ) {
      const { showError } = useCustomAlert()
      const utm = await getStoredUtmParams()

      const cognitoAttributes = [
        new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
        new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
        new CognitoUserAttribute({
          Name: 'custom:hearAboutBalance',
          Value: hearAboutBalance,
        }),
        new CognitoUserAttribute({
          Name: 'custom:agreedToTerms',
          Value: agreeToTerms.toString(),
        }),
      ]

      // Names must match CDK user pool schema (resources-stack.ts): camelCase after custom:
      if (utm.utm_source) {
        cognitoAttributes.push(
          new CognitoUserAttribute({
            Name: 'custom:utmSource',
            Value: utm.utm_source,
          }),
        )
      }
      if (utm.utm_medium) {
        cognitoAttributes.push(
          new CognitoUserAttribute({
            Name: 'custom:utmMedium',
            Value: utm.utm_medium,
          }),
        )
      }
      if (utm.utm_campaign) {
        cognitoAttributes.push(
          new CognitoUserAttribute({
            Name: 'custom:utmCampaign',
            Value: utm.utm_campaign,
          }),
        )
      }
      if (utm.utm_content) {
        cognitoAttributes.push(
          new CognitoUserAttribute({
            Name: 'custom:utmContent',
            Value: utm.utm_content,
          }),
        )
      }
      if (utm.utm_term) {
        cognitoAttributes.push(
          new CognitoUserAttribute({
            Name: 'custom:utmTerm',
            Value: utm.utm_term,
          }),
        )
      }
      if (utm.campaign_id) {
        cognitoAttributes.push(
          new CognitoUserAttribute({
            Name: 'custom:campaignId',
            Value: utm.campaign_id,
          }),
        )
      }

      return await new Promise((resolve, reject) => {
        userPool.signUp(
          email,
          password,
          cognitoAttributes,
          [],
          (err, result) => {
            if (err) {
              showError(err.message || JSON.stringify(err))
              reject()
            } else {
              this.user = new User(
                email,
                firstName,
                lastName,
                '',
                '',
                '',
                '',
                '',
              )
              this.onboardingComplete = false
              this.signupData = {
                firstName,
                lastName,
                email,
                password,
                hearAboutBalance,
              }
              resolve(result)
            }
          },
        )
      })
    },
    async confirmRegistration(code: string) {
      const { showError } = useCustomAlert()

      if (!this.user) {
        return undefined
      }

      const cognitoUser = new CognitoUser({
        Username: this.user.email,
        Pool: userPool,
      })

      return await new Promise((resolve, reject) => {
        cognitoUser.confirmRegistration(code, true, (err, result) => {
          if (err) {
            showError(err.message || JSON.stringify(err))
            reject(err)
          } else {
            resolve(result)
          }
        })
      })
    },
    async resendConfirmationCode() {
      const { showError } = useCustomAlert()

      if (!this.user) {
        showError('No user found. Please restart the signup process.', () => {
          this.router.push('/auth/signup')
        })
        return Promise.reject(
          new Error('No user found. Please restart the signup process.'),
        )
      }

      const cognitoUser = new CognitoUser({
        Username: this.user.email,
        Pool: userPool,
      })

      return await new Promise((resolve, reject) => {
        cognitoUser.resendConfirmationCode((err, result) => {
          if (err) {
            console.error('Failed to resend code:', err)
            showError('Failed to resend verification code. Please try again.')
            reject(err)
          } else {
            resolve(result)
          }
        })
      })
    },

    async changePassword(oldPassword: string, newPassword: string) {
      if (!this.user) {
        return Promise.reject(new Error('No User found'))
      }

      let cognitoUser: any
      cognitoUser = userPool.getCurrentUser()
      if (!cognitoUser) {
        cognitoUser = new CognitoUser({
          Username: this.user.email,
          Pool: userPool,
        })
      }

      return new Promise((resolve, reject) => {
        cognitoUser.getSession((err: any, session: any) => {
          if (err || !session?.isValid()) {
            console.error('Session error:', err || 'Invalid session')
            reject(new Error('User is not authenticated'))
            return
          }

          cognitoUser.changePassword(
            oldPassword,
            newPassword,
            (err, result) => {
              if (err) {
                console.error('Error changing password:', err)
                reject(err)
                return
              }
              console.log('Password changed successfully:', result)
              resolve(result)
            },
          )
        })
      })
    },

    async sendForgotPasswordOTP(email?: string) {
      let cognitoUser: CognitoUser | null = userPool.getCurrentUser()

      if (email) {
        cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool,
        })
      } else {
        if (!this.user) {
          return Promise.reject(new Error('No User found'))
        }
        if (!cognitoUser) {
          cognitoUser = new CognitoUser({
            Username: this.user.email,
            Pool: userPool,
          })
        }
      }

      return new Promise((resolve, reject) => {
        cognitoUser!.forgotPassword({
          onSuccess: (result) => {
            console.log('sent password reset verficaton code')
            resolve(result)
            return
          },
          onFailure: (err) => {
            console.log('error in sending verification code')
            reject(err)
            return
          },
        })
      })
    },

    async confirmForgotPasswordReset(
      code: string,
      newPassword: string,
      email?: string,
    ) {
      let cognitoUser: CognitoUser | null = userPool.getCurrentUser()

      if (email) {
        cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool,
        })
      } else {
        if (!this.user) {
          return Promise.reject(new Error('No User found'))
        }
        if (!cognitoUser) {
          cognitoUser = new CognitoUser({
            Username: this.user.email,
            Pool: userPool,
          })
        }
      }
      return new Promise((resolve, reject) => {
        cognitoUser.confirmPassword(code, newPassword, {
          onSuccess() {
            resolve('')
          },
          onFailure(err) {
            reject(err)
          },
        })
      })
    },

    async logout() {
      const cognitoUser = userPool.getCurrentUser()
      if (cognitoUser) {
        cognitoUser.signOut()
      }

      // Clear auth store state
      this.token = null
      this.refreshToken = null
      this.user = null
      this.interceptor = null
      this.onboardingComplete = true
      this.refreshTokenBiometric = null
      this.usernameBiometric = null
      this.signupData = null

      // Clear axios default headers
      this.axios.defaults.headers.common['Authorization'] = undefined

      // Remove the interceptor
      if (this.interceptor !== null) {
        this.axios.interceptors.response.eject(this.interceptor)
        this.interceptor = null
      }

      // Clear ionic storage
      await this.ionicStorage.remove('user')
      await this.ionicStorage.remove('token')
      await this.ionicStorage.remove('refreshToken')
      await this.ionicStorage.clear()

      // Clear other stores
      const plaidItemsStore = usePlaidItemsStore()
      const categoriesStore = useCategoriesStore()
      const transactionsStore = useTransactionsStore()

      plaidItemsStore.$reset()
      categoriesStore.$reset()
      transactionsStore.$reset()

      // Clear any sessionStorage or localStorage items
      sessionStorage.clear()
      localStorage.clear()

      // Navigate to login page
      this.router.replace('/auth/login')
    },
    setAuthHeader() {
      this.axios.defaults.headers.common['Authorization'] = this.token
    },
    async loadRefreshToken() {
      this.refreshToken = await this.ionicStorage.get('refreshToken')
      this.user = await this.ionicStorage.get('user')
    },
    async refreshSession(from: 'normal' | 'biometric' = 'normal') {
      let cognitoUser: any
      cognitoUser = userPool.getCurrentUser()
      if (!cognitoUser) {
        if (from === 'normal') {
          if (!this.user || !this.user.email) {
            throw new Error('No user')
          }
          cognitoUser = new CognitoUser({
            Username: this.user.email,
            Pool: userPool,
          })
        } else if (from === 'biometric') {
          if (!this.usernameBiometric) {
            throw new Error('No user')
          }
          cognitoUser = new CognitoUser({
            Username: this.usernameBiometric,
            Pool: userPool,
          })
        }
      }
      if (!this.refreshToken) {
        console.error('No refresh token')
      }

      const refreshToken = new CognitoRefreshToken({
        RefreshToken:
          from === 'normal' ? this.refreshToken! : this.refreshTokenBiometric!,
      })
      return await new Promise((resolve, reject) => {
        cognitoUser.refreshSession(
          refreshToken,
          async (err: any, session: any) => {
            if (err) {
              console.log('Error refreshing session', err)
              this.logout()
              if (from === 'biometric') {
                this.clearBiometrics()
              }
              reject(err)
            } else {
              this.token = session.getIdToken().getJwtToken()
              this.setAuthHeader()
              // Get user data after refreshing session to ensure PostHog identification
              await this.getUser()
              resolve(session)
            }
          },
        )
      })
    },
    setInterceptor() {
      this.interceptor = this.axios.interceptors.response.use(
        (response) => response,
        async (error) => {
          if (error) {
            if (error.response && error.response.status === 500) {
              return Promise.reject(error)
            }
            try {
              const session = (await this.refreshSession()) as any
              // Retry the original request with the new token
              return axios({
                ...error.config,
                headers: { Authorization: session.getIdToken().getJwtToken() },
              })
            } catch (refreshError) {
              // If refresh token is expired or any other error occurs, logout
              this.logout()
              this.router.push('/auth/login')
            }
          }
          return Promise.reject(error)
        },
      )
    },
    async checkBiometricAvailability() {
      return (await BiometricAuth.checkBiometry()).isAvailable
    },
    async getAvailableBiometricTypes(): Promise<string[]> {
      const biometryTypes = (await BiometricAuth.checkBiometry()).biometryTypes
      return biometryTypes.map((type) => BiometryTypesCustom[type])
    },
    async fetchStoredBiometricToken() {
      try {
        const { value: refreshToken } = await SecureStoragePlugin.get({
          key: 'refreshToken',
        })
        this.refreshTokenBiometric = refreshToken
        const { value: username } = await SecureStoragePlugin.get({
          key: 'username',
        })
        this.usernameBiometric = username
      } catch (e) {
        console.error('error in getting biometrics stored auth', e)
        this.refreshTokenBiometric = null
      }
    },
    async storeBiometricCredentials() {
      try {
        if (!this.user?.email || !this.refreshToken) {
          throw new Error('No user')
        }
        await SecureStoragePlugin.set({
          key: 'refreshToken',
          value: this.refreshToken,
        })
        await SecureStoragePlugin.set({
          key: 'username',
          value: this.user.email,
        })
        this.refreshTokenBiometric = this.refreshToken
      } catch (error) {
        console.error('Error storing credentials', error)
        throw error
      }
    },

    async clearBiometrics() {
      try {
        await SecureStoragePlugin.clear()
        this.refreshTokenBiometric = null
        this.usernameBiometric = null
      } catch (error) {
        console.error(error)
      }
    },

    async authenticateWithBiometrics() {
      try {
        const isBiometricAvailable = await this.checkBiometricAvailability()
        const biometricLabel =
          BiometryTypesCustom[
            (await BiometricAuth.checkBiometry()).biometryTypes[0]
          ]

        if (!isBiometricAvailable) {
          throw new Error(`${biometricLabel} is not available`)
        }
        await this.fetchStoredBiometricToken()

        if (!this.refreshTokenBiometric || !this.usernameBiometric) {
          throw new Error(`No saved ${biometricLabel} tokens`)
        }

        await BiometricAuth.authenticate({
          reason: `Login with ${biometricLabel}`,
          androidTitle: `${biometricLabel} Login`,
        })
        this.refreshToken = this.refreshTokenBiometric
        await this.refreshSession('biometric')
      } catch (error) {
        console.error('Biometric authentication failed', error)
        throw error
      }
    },

    setSignupData(data: {
      firstName: string
      lastName: string
      email: string
      password?: string
      hearAboutBalance?: string
    }) {
      this.signupData = data
    },

    async updateMarketingPreference(wantsMarketingEmails: boolean) {
      try {
        const response = await this.axios.put('/user/marketing-preference', {
          wantsMarketingEmails,
        })
        if (this.user) {
          this.user.wantsMarketingEmails = wantsMarketingEmails
        }
        await this.ionicStorage.set('user', toRaw(this.user))
        return response.data
      } catch (error) {
        console.error('Failed to update marketing preference:', error)
        throw error
      }
    },
  },
})
